import React, { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import PageHeader from "@/components/common/PageHeader";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Search, BookOpen, ChevronRight, ArrowLeft, FileText, Tag, Sparkles } from "lucide-react";

const MODULE_LABELS = {
  sales: "Sales", purchase: "Purchase", inventory: "Inventory",
  manufacturing: "Manufacturing", banking: "Banking", gate_pass: "Gate Pass",
  compliance: "Compliance", dsc: "Digital Signature", hr: "HR",
  migration: "Migration", brochure: "Brochure", distribution: "Distribution",
};

// Minimal markdown → HTML (no external dep). Handles headings, lists, code, bold, italics, tables, links.
function md(src) {
  if (!src) return "";
  let s = src.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
  // Code blocks
  s = s.replace(/```([\s\S]*?)```/g, (_, c) => `<pre class="bg-slate-100 rounded-sm p-3 my-2 text-[12px] font-mono overflow-x-auto">${c.trim()}</pre>`);
  // Tables
  s = s.replace(/((?:^\|.*\|\n?)+)/gm, (block) => {
    const lines = block.trim().split("\n");
    if (lines.length < 2) return block;
    const cells = (l) => l.split("|").slice(1, -1).map((c) => c.trim());
    const head = cells(lines[0]);
    const body = lines.slice(2).map(cells);
    const th = head.map((c) => `<th class="text-left p-2 border-b font-semibold">${c}</th>`).join("");
    const tr = body.map((r) => `<tr>${r.map((c) => `<td class="p-2 border-b">${c}</td>`).join("")}</tr>`).join("");
    return `<table class="w-full text-sm my-3 border rounded-sm">${th ? `<thead class="bg-muted/40"><tr>${th}</tr></thead>` : ""}<tbody>${tr}</tbody></table>`;
  });
  // Headings
  s = s.replace(/^### (.*)$/gm, '<h3 class="font-heading font-semibold text-base mt-4 mb-1">$1</h3>');
  s = s.replace(/^## (.*)$/gm, '<h2 class="font-heading font-bold text-lg mt-5 mb-2">$1</h2>');
  s = s.replace(/^# (.*)$/gm, '<h1 class="font-heading font-bold text-xl mt-5 mb-2">$1</h1>');
  // Bold / italic / inline code
  s = s.replace(/\*\*(.+?)\*\*/g, "<b>$1</b>");
  s = s.replace(/\*(.+?)\*/g, "<i>$1</i>");
  s = s.replace(/`([^`]+)`/g, '<code class="bg-slate-100 px-1 py-0.5 rounded-sm text-[12px] font-mono">$1</code>');
  // Ordered lists
  s = s.replace(/(^\d+\.\s.*(?:\n\d+\.\s.*)*)/gm, (m) => `<ol class="list-decimal ml-5 my-2 space-y-1">${m.split("\n").map((l) => `<li>${l.replace(/^\d+\.\s/, "")}</li>`).join("")}</ol>`);
  // Unordered lists
  s = s.replace(/(^-\s.*(?:\n-\s.*)*)/gm, (m) => `<ul class="list-disc ml-5 my-2 space-y-1">${m.split("\n").map((l) => `<li>${l.replace(/^-\s/, "")}</li>`).join("")}</ul>`);
  // Paragraphs from blank-line breaks
  s = s.split(/\n{2,}/).map((chunk) => chunk.match(/^<(h\d|ul|ol|pre|table)/) ? chunk : `<p class="my-2">${chunk.replace(/\n/g, "<br/>")}</p>`).join("");
  return s;
}

export default function HelpCenter() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [articles, setArticles] = useState([]);
  const [current, setCurrent] = useState(null);
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => { api.get("/help/articles").then((r) => setArticles(r.data)); }, []);
  useEffect(() => {
    if (slug) {
      setBusy(true);
      api.get(`/help/articles/${slug}`).then((r) => setCurrent(r.data)).finally(() => setBusy(false));
    } else setCurrent(null);
  }, [slug]);

  const filtered = useMemo(() => {
    if (!q.trim()) return articles;
    const needles = q.toLowerCase().split(/\s+/).filter(Boolean);
    return articles.filter((a) => {
      const hay = `${a.title} ${a.category} ${(a.tags || []).join(" ")}`.toLowerCase();
      return needles.every((n) => hay.includes(n));
    });
  }, [articles, q]);

  const grouped = useMemo(() => {
    const g = {};
    filtered.forEach((a) => { (g[a.module] = g[a.module] || []).push(a); });
    return Object.entries(g).sort((a, b) => a[0].localeCompare(b[0]));
  }, [filtered]);

  // ----- Article view -----
  if (slug) {
    return (
      <div className="p-6 lg:p-8 space-y-4 max-w-4xl" data-testid="help-article">
        <Button variant="ghost" size="sm" onClick={() => navigate("/app/help")} className="gap-1.5 rounded-sm" data-testid="help-back">
          <ArrowLeft className="w-4 h-4" /> All articles
        </Button>
        {busy && <Card className="p-8 text-muted-foreground">Loading…</Card>}
        {!busy && current && (
          <Card className="p-6 lg:p-8 rounded-sm">
            <div className="flex items-center gap-2 text-[10px] font-mono uppercase tracking-widest text-accent">
              <BookOpen className="w-3 h-3" /> {MODULE_LABELS[current.module] || current.module} · {current.category}
            </div>
            <h1 className="font-heading text-3xl font-bold mt-2">{current.title}</h1>
            <div className="flex gap-1 mt-3 flex-wrap">
              {(current.tags || []).map((t) => (
                <span key={t} className="text-[10px] font-mono px-2 py-0.5 rounded-sm bg-accent/10 text-accent flex items-center gap-1"><Tag className="w-2.5 h-2.5" /> {t}</span>
              ))}
            </div>
            <div className="prose prose-sm max-w-none mt-6 text-[14px] leading-6" dangerouslySetInnerHTML={{ __html: md(current.body_md) }} />
            {current.updated_at && <div className="text-[11px] text-muted-foreground mt-6 pt-4 border-t">Last updated {new Date(current.updated_at).toLocaleDateString("en-IN")} by {current.updated_by}</div>}
          </Card>
        )}
      </div>
    );
  }

  // ----- Index -----
  return (
    <div className="p-6 lg:p-8 space-y-6" data-testid="help-index">
      <PageHeader eyebrow="Support" title="Help Center"
        description="Searchable guides for every feature in Zenith 360 — updated as the product evolves." />

      <Card className="p-4 rounded-sm border-2 border-accent/40">
        <div className="flex items-center gap-2">
          <Search className="w-4 h-4 text-accent" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search articles — try 'BOM', 'ITR', 'GSTR-1', 'DSC'…" className="rounded-sm border-0 focus-visible:ring-0" data-testid="help-search" autoFocus />
          <span className="text-[11px] font-mono text-muted-foreground">{filtered.length} results</span>
        </div>
      </Card>

      {grouped.length === 0 && (
        <Card className="p-10 rounded-sm text-center text-muted-foreground italic">
          {articles.length === 0 ? "No articles yet — an admin can seed the defaults from Settings → Help." : "No matches for that search."}
        </Card>
      )}

      {grouped.map(([mod, arts]) => (
        <Card key={mod} className="p-5 rounded-sm">
          <div className="text-[10px] font-mono uppercase tracking-widest text-accent flex items-center gap-1"><Sparkles className="w-3 h-3" /> {MODULE_LABELS[mod] || mod}</div>
          <div className="grid md:grid-cols-2 gap-2 mt-3">
            {arts.map((a) => (
              <button key={a.slug} onClick={() => navigate(`/app/help/${a.slug}`)}
                className="text-left p-3 rounded-sm border border-border hover:border-accent/40 hover:bg-accent/5 transition-colors group"
                data-testid={`help-card-${a.slug}`}>
                <div className="flex items-start gap-2">
                  <FileText className="w-4 h-4 text-accent flex-shrink-0 mt-0.5" />
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold text-sm group-hover:text-accent transition-colors">{a.title}</div>
                    <div className="text-[11px] text-muted-foreground mt-0.5">{a.category}</div>
                  </div>
                  <ChevronRight className="w-4 h-4 text-muted-foreground group-hover:text-accent transition-colors flex-shrink-0 mt-0.5" />
                </div>
              </button>
            ))}
          </div>
        </Card>
      ))}
    </div>
  );
}
